import React, { Fragment } from 'react';
import Loader from 'react-loader';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Glyphicon, OverlayTrigger, Tooltip } from 'react-bootstrap';
import uuid from 'uuid/v4';

import ProgressBar from '../ProgressBar';

import './Iframe.css';

const iframeTitleTooltip = tooltipMessage => (
  <Tooltip id="data-bar__item-tooltip">{tooltipMessage}</Tooltip>
);

class Iframe extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, loaded: false, key: uuid() };
    this.contentLoaded = this.contentLoaded.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { url: nextUrl } = this.props;
    const { url: prevUrl } = prevProps;

    if (nextUrl !== prevUrl) {
      this.setState(() => ({ loading: true, loaded: false, key: uuid() })); // eslint-disable-line react/no-did-update-set-state
    }
  }

  contentLoaded() {
    this.setState({ loading: false, loaded: true }, () => {
      if (this.props.onLoadFunc) {
        this.props.onLoadFunc(this.props.iframeKey);
      }
    });
  }

  renderLoader() {
    const { blocked, timeout } = this.props;
    const { loading, loaded, key } = this.state;

    return !blocked ? (
      <div className="iframe__loader-wrapper">
        <ProgressBar key={key} completed={loaded} timeout={timeout} />
        <Loader
          lines={13}
          loaded={!loading}
          loadedClassName="iframe--loaded"
          parentClassName="iframe__loader"
          width={2}
        />
      </div>
    ) : null;
  }

  renderContent() {
    const {
      blocked,
      blockedMessage,
      canScroll,
      height,
      title,
      url,
      width
    } = this.props;

    return (
      <Fragment>
        {blocked && (
          <div className="iframe__message-blocked" style={{ width, height }}>
            <Glyphicon glyph="lock" className="iframe__message-blocked-icon" />
            <p className="iframe__message-blocked-content">{blockedMessage}</p>
          </div>
        )}

        {url && (
          <iframe
            onLoad={this.contentLoaded}
            key={this.state.key}
            src={url}
            title={title}
            height={this.state.loading ? 0 : height}
            width={this.state.loading ? 0 : width}
            allowtransparency="true" //eslint-disable-line
            frameBorder={0}
            scrolling={canScroll ? '' : 'no'}
          />
        )}
      </Fragment>
    );
  }

  render() {
    const {
      title,
      height,
      width,
      blocked,
      useDefaultTitle,
      seeAllLabel,
      seeAllLink,
      tooltip
    } = this.props;

    const parentWidth = width !== '100%' ? { width } : null;
    const wrapperClassName = classnames('iframe-root', {
      'iframe--blocked': blocked,
      'iframe--full-height': height === '100%',
      'iframe--loading': this.state.loading
    });

    const wrappedTitle = tooltip ? (
      <Fragment>
        <h3 className="iframe__title iframe__title--with-tooltip">{title}</h3>
        <OverlayTrigger placement="top" overlay={iframeTitleTooltip(tooltip)}>
          <i className="iframe__title-tooltip fa fa-info-circle" />
        </OverlayTrigger>
      </Fragment>
    ) : (
      <h3 className="iframe__title">{title}</h3>
    );

    return (
      <div className={wrapperClassName} style={parentWidth}>
        {useDefaultTitle && (
          <div className="iframe__header">
            {wrappedTitle}
            {seeAllLink && (
              <a className="iframe__more-link" href={seeAllLink}>
                {seeAllLabel}
              </a>
            )}
          </div>
        )}

        {this.renderLoader()}
        {this.renderContent()}
      </div>
    );
  }
}

Iframe.propTypes = {
  blocked: PropTypes.bool,
  blockedMessage: PropTypes.string,
  canScroll: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  seeAllLabel: PropTypes.string,
  seeAllLink: PropTypes.string,
  timeout: PropTypes.number,
  title: PropTypes.string,
  tooltip: PropTypes.string,
  url: PropTypes.string.isRequired,
  useDefaultTitle: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

Iframe.defaultProps = {
  blocked: false,
  blockedMessage: '',
  canScroll: true,
  height: 280,
  seeAllLabel: 'See All',
  seeAllLink: '',
  timeout: 30,
  title: '',
  tooltip: '',
  useDefaultTitle: true,
  width: '100%'
};

export default Iframe;
